// src/components/auth/AuthModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { FcGoogle } from "react-icons/fc";
import { SiEthereum, SiSolana } from "react-icons/si";
import { IoClose } from "react-icons/io5";
import { FaSpinner } from "react-icons/fa";

const GOOGLE_BTN_OPTIONS = { theme: "outline", size: "large", width: 320, shape: "pill" };

export default function AuthModal({ onClose }) {
  const {
    loginWithGoogleIdToken,
    loginWithEvm,
    loginWithSol,
    linkWalletEvm,
    linkWalletSol,
    loginWithEmailPassword,
    registerWithEmailPassword,
    user,
  } = useAuth();

  // --- UI state
  const [mode, setMode] = useState("login"); // 'login' | 'register' (default login)
  const [busy, setBusy] = useState(null);
  const [googleReady, setGoogleReady] = useState(false);

  // --- Email form state
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [emailErr, setEmailErr] = useState("");

  const googleDiv = useRef(null);
  const closeBtnRef = useRef(null);

  // Modal lifecycle (focus, esc, scroll lock)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    closeBtnRef.current?.focus?.();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Load Google script once (if not present)
  useEffect(() => {
    if (window.google) { setGoogleReady(true); return; }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true; s.defer = true;
    s.onload = () => setGoogleReady(true);
    document.body.appendChild(s);
  }, []);

  // Render Google button
  useEffect(() => {
    if (!googleReady || !googleDiv.current || !window.google) return;
    window.google.accounts.id.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      callback: async (resp) => {
        try { setBusy("google"); await loginWithGoogleIdToken(resp.credential); onClose?.(); }
        catch { alert("Google login failed"); }
        finally { setBusy(null); }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });
    try { window.google.accounts.id.renderButton(googleDiv.current, GOOGLE_BTN_OPTIONS); } catch {}
  }, [googleReady, loginWithGoogleIdToken, onClose]);

  const ProviderButton = ({ onClick, icon, label, colorClass, loading }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!!loading}
      className={[
        "w-full rounded-xl px-4 py-3 flex items-center justify-center gap-3",
        "transition-colors border border-white/10 hover:bg-white/[0.06] disabled:opacity-60",
        colorClass,
      ].join(" ")}
    >
      {loading ? <FaSpinner className="animate-spin" /> : icon}
      <span className="font-medium">{label}</span>
    </button>
  );

  async function handleEmailSubmit(e) {
    e?.preventDefault?.();
    setEmailErr("");
    if (!email || !pass) { setEmailErr("Email and password are required"); return; }
    if (mode === "register" && pass !== pass2) { setEmailErr("Passwords do not match"); return; }
    try {
      setBusy("email");
      if (mode === "register") await registerWithEmailPassword(email, pass);
      else await loginWithEmailPassword(email, pass);
      onClose?.();
    } catch (err) {
      const msg = err?.message || "Authentication failed";
      setEmailErr(msg);
      if (mode === "login" && /not.*found|invalid/i.test(msg)) {
        // gently nudge to register when login fails for unknown user
        setMode("register");
      }
    } finally {
      setBusy(null);
    }
  }

  const Tabs = () => (
    <div className="flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10">
      {["login", "register"].map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => setMode(m)}
          className={[
            "px-4 py-1.5 text-sm rounded-lg transition",
            mode === m ? "bg-white text-black" : "text-white/80 hover:bg-white/10",
          ].join(" ")}
        >
          {m === "login" ? "Login" : "Register"}
        </button>
      ))}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.currentTarget === e.target) onClose?.(); }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md mx-4 rounded-2xl border border-white/10 shadow-2xl bg-black/95">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/10">
            <img src="/images/logo.png" alt="logo" className="w-7 h-7 rounded-lg" onError={(e)=> (e.currentTarget.style.display='none')} />
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold text-white">
              {user ? "Account & Wallets" : (mode === "login" ? "Welcome back" : "Create your account")}
            </div>
            <div className="text-[12px] text-white/60">
              {user ? "Link your wallets or continue" :
                (mode === "login" ? "Sign in with Google, email, or your wallet" : "Register with email (or continue with a provider)")}
            </div>
          </div>
          <button
            ref={closeBtnRef}
            aria-label="Close"
            className="ml-auto p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
            onClick={() => onClose?.()}
          >
            <IoClose className="text-xl" />
          </button>
        </div>
        {!user && <div className="ml-auto w-fit mx-auto mt-3"><Tabs /></div>}

        {/* Body */}
        <div className="px-5 pb-5 pt-4">
          {!user ? (
            <>
              {/* Google */}
              <div className="mb-3">
                <div ref={googleDiv} className="flex items-center justify-center" style={{ minHeight: 44 }} />
                {!googleReady && (
                  <ProviderButton
                    label="Continue with Google"
                    icon={<FcGoogle className="text-xl" />}
                    colorClass="bg-white/5"
                    loading={busy === "google"}
                  />
                )}
              </div>

              <div className="relative my-4">
                <div className="h-px bg-white/10" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="px-2 text-xs text-white/40 bg-black/95">or</span>
                </div>
              </div>

              {/* Email form (switches by mode) */}
              <form onSubmit={handleEmailSubmit} className="rounded-xl border border-white/10 p-3 bg-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-white/90">
                    {mode === "login" ? "Login with Email" : "Register with Email"}
                  </div>
                  <button
                    type="button"
                    className="text-xs text-white/60 hover:text-white/90"
                    onClick={() => setMode(mode === "login" ? "register" : "login")}
                  >
                    {mode === "login" ? "Need an account?" : "Have an account?"}
                  </button>
                </div>
                <div className="space-y-2">
                  <input
                    type="email"
                    placeholder="you@example.com"
                    className="w-full px-3 py-2 text-sm rounded-lg bg-gray-800 border border-white/10 outline-none"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    className="w-full px-3 py-2 text-sm rounded-lg bg-gray-800 border border-white/10 outline-none"
                    value={pass}
                    onChange={(e) => setPass(e.target.value)}
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                  />
                  {mode === "register" && (
                    <input
                      type="password"
                      placeholder="Confirm password"
                      className="w-full px-3 py-2 text-sm rounded-lg bg-gray-800 border border-white/10 outline-none"
                      value={pass2}
                      onChange={(e) => setPass2(e.target.value)}
                      autoComplete="new-password"
                    />
                  )}
                  {emailErr && <div className="text-xs text-rose-400">{emailErr}</div>}
                  <button
                    type="submit"
                    disabled={busy === "email"}
                    className="w-full rounded-xl px-4 py-2 bg-[#1E9BF0] text-white hover:bg-[#1a8cd8] transition disabled:opacity-60"
                  >
                    {busy === "email" ? "Please wait…" : (mode === "login" ? "Login" : "Register")}
                  </button>
                </div>
              </form>

              {/* Wallets */}
              <div className="space-y-2 mt-4">
                <ProviderButton
                  label={mode === "login" ? "Continue with EVM Wallet" : "Use EVM Wallet"}
                  icon={<SiEthereum className="text-lg" />}
                  colorClass="bg-white/5"
                  loading={busy === "evm"}
                  onClick={async () => {
                    try { setBusy("evm"); await loginWithEvm(); onClose?.(); }
                    catch (e) { alert(e?.message || "EVM wallet auth failed"); }
                    finally { setBusy(null); }
                  }}
                />
                <ProviderButton
                  label={mode === "login" ? "Continue with Solana Wallet" : "Use Solana Wallet"}
                  icon={<SiSolana className="text-lg" />}
                  colorClass="bg-white/5"
                  loading={busy === "sol"}
                  onClick={async () => {
                    try { setBusy("sol"); await loginWithSol(); onClose?.(); }
                    catch (e) { alert(e?.message || "Solana wallet auth failed"); }
                    finally { setBusy(null); }
                  }}
                />
              </div>

              <div className="mt-4 text-[11px] text-white/50 leading-relaxed">
                By continuing you agree to our Terms and acknowledge our Privacy Policy.
              </div>
            </>
          ) : (
            <>
              <div className="p-3 mb-3 rounded-lg bg-white/5 border border-white/10">
                <div className="text-sm text-white/80">
                  Signed in as <span className="font-semibold">{user.email || user.id}</span>
                </div>
                <div className="mt-1 text-xs text-white/60">Bind a wallet so you can log in with it later.</div>
              </div>

              <div className="space-y-2">
                <ProviderButton
                  label="Link EVM Wallet"
                  icon={<SiEthereum className="text-lg" />}
                  colorClass="bg-white/5"
                  loading={busy === "link-evm"}
                  onClick={async () => {
                    try { setBusy("link-evm"); await linkWalletEvm(); alert("EVM wallet linked"); }
                    catch (e) { alert(e?.message || "Failed to link EVM wallet"); }
                    finally { setBusy(null); }
                  }}
                />
                <ProviderButton
                  label="Link Solana Wallet"
                  icon={<SiSolana className="text-lg" />}
                  colorClass="bg-white/5"
                  loading={busy === "link-sol"}
                  onClick={async () => {
                    try { setBusy("link-sol"); await linkWalletSol(); alert("Solana wallet linked"); }
                    catch (e) { alert(e?.message || "Failed to link Solana wallet"); }
                    finally { setBusy(null); }
                  }}
                />
              </div>

              <div className="mt-5">
                <button
                  className="w-full px-4 py-2 text-sm rounded-xl bg-[#1E9BF0] text-white hover:bg-[#1a8cd8] transition"
                  onClick={() => onClose?.()}
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
